import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, Empty, message, Modal } from 'antd';
import { PlayCircleOutlined, CloseOutlined, EyeOutlined, DownloadOutlined, LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { videoTitleAPI, videoContentAPI, systemAPI } from '../../services/api';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './ResourceExtension.css';
import redFrameIcon from '../../static/image/红框.png';
import pdfIcon from '../../static/image/pdf.png';
import docxIcon from '../../static/image/docx.png';
import pptIcon from '../../static/image/ppt.png';

// 配置 PDF.js worker - 使用可靠的 CDN
// 优先使用 unpkg，它直接从 npm 获取包，版本号始终准确
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// 备选方案（如果上面的不行，可以尝试以下任一方案）:
// pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
// pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
 
/**
 * 检测浏览器是否支持 H.265 (HEVC) 视频解码
 * @returns {boolean} 是否支持 H.265
 */
const canPlayH265 = () => {
  try {
    // 检测是否支持 MediaSource API
    if (typeof MediaSource === 'undefined') {
      return false;
    }
    // 检测是否支持 H.265/HEVC 编码
    // hvc1.1.6.L93.90 是常见的 H.265 Main Profile Level 3.1 编码
    return MediaSource.isTypeSupported('video/mp4; codecs="hvc1.1.6.L93.90"');
  } catch (error) {
    console.warn('[视频解码检测] 检测失败:', error);
    return false;
  }
};

/**
 * 检测浏览器是否支持在线阅读 PDF
 * @returns {boolean} 是否支持 PDF 在线阅读
 */
const canReadPdfOnline = () => {
  try {
    // 检测是否支持 Canvas API（PDF.js 依赖）
    const canvas = document.createElement('canvas');
    if (!canvas.getContext || !canvas.getContext('2d')) {
      console.warn('[PDF阅读能力检测] 不支持 Canvas API');
      return false;
    }
    
    // 检测是否支持 Promise（PDF.js 依赖）
    if (typeof Promise === 'undefined') {
      console.warn('[PDF阅读能力检测] 不支持 Promise');
      return false;
    }
    
    // 检测是否支持 Promise.withResolvers（PDF.js 3.x+ 依赖，ES2024 新特性）
    // 这是导致 Win7 等旧系统无法使用 PDF.js 的关键原因
    if (typeof Promise.withResolvers !== 'function') {
      console.warn('[PDF阅读能力检测] 不支持 Promise.withResolvers (ES2024)');
      return false;
    }
    
    // 检测是否支持 Uint8Array（PDF.js 依赖）
    if (typeof Uint8Array === 'undefined') {
      console.warn('[PDF阅读能力检测] 不支持 Uint8Array');
      return false;
    }
    
    // 检测是否支持 Blob（用于 PDF 文件处理）
    if (typeof Blob === 'undefined') {
      console.warn('[PDF阅读能力检测] 不支持 Blob');
      return false;
    }
    
    // 所有必需的 API 都支持
    return true;
  } catch (error) {
    console.warn('[PDF阅读能力检测] 检测失败:', error);
    return false;
  }
};

/**
 * 根据浏览器视频解码能力获取视频资源基础URL
 * 支持 H.265 的浏览器使用 video2 服务器（更高效的编码）
 * 不支持 H.265 的浏览器（如 Win7）自动使用 video264 服务器（H.264 编码）
 */
const getVideoBaseUrl = () => {
  const supportsH265 = canPlayH265();
  const baseUrl = supportsH265
    ? 'https://video2.cd.xdo.icu:443/' 
    : 'https://video264.cd.xdo.icu:443/';
  
  console.log(`[视频资源] H.265解码支持: ${supportsH265 ? '✓ 支持' : '✗ 不支持'}, 使用服务器: ${baseUrl}`);
  return baseUrl;
};

// 视频资源基础URL
const VIDEO_BASE_URL = getVideoBaseUrl();

// 检测并输出 PDF 在线阅读能力
const PDF_ONLINE_SUPPORT = canReadPdfOnline();
console.log(`[PDF阅读] 在线阅读支持: ${PDF_ONLINE_SUPPORT ? '✓ 支持' : '✗ 不支持 (将提示用户下载后查阅)'}`);
if (!PDF_ONLINE_SUPPORT) {
  console.warn('[PDF阅读] 检测到当前浏览器不支持 PDF 在线阅读，可能是 Windows 7 或旧版浏览器');
}

// 一级标题映射
const FIRST_TITLE_MAP = {
  '/good-reading': { id: 1, name: '好篇阅读' },
  '/premium-teaching': { id: 2, name: '培优精讲' },
  '/special-breakthrough': { id: 3, name: '专项突破' },
  '/exam-strategy': { id: 4, name: '备考攻略' },
  '/resource-download': { id: 5, name: '资源下载' },
};

const ResourceExtension = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const [videoDataVersion, setVideoDataVersion] = useState(0); // videoData 版本号
  const [selectedSecondId, setSelectedSecondId] = useState(null);
  const [selectedThirdId, setSelectedThirdId] = useState(null);
  const [videoContents, setVideoContents] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null); // 当前播放的视频信息
  const [isPlayerVisible, setIsPlayerVisible] = useState(false); // 播放器是否可见
  const [previewVisible, setPreviewVisible] = useState(false); // 预览弹窗是否可见
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0); // 当前预览的文件索引
  const [numPages, setNumPages] = useState(null); // PDF 总页数
  const [pageNumber, setPageNumber] = useState(1); // 当前 PDF 页码
  const [pdfScale, setPdfScale] = useState(1.2); // PDF 缩放比例
  const [pdfError, setPdfError] = useState(null); // PDF 加载错误信息
  const [isDoublePage, setIsDoublePage] = useState(false); // 是否双页显示
  
  // 🔥 使用 ref 追踪是否正在加载标题数据，防止竞态条件
  const isLoadingTitlesRef = useRef(false);

  // 获取当前页面的一级标题信息
  const currentTitle = FIRST_TITLE_MAP[location.pathname];

  // 使用 useMemo 优化 PDF 配置，避免不必要的重新加载
  const pdfFile = useMemo(() => {
    if (!videoContents[currentPreviewIndex]) return null;
    const currentFile = videoContents[currentPreviewIndex];
    return {
      url: `${VIDEO_BASE_URL}${currentFile.title}`,
      withCredentials: false,
    };
  }, [currentPreviewIndex, videoContents]);

  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  /**
   * 格式化时间或文件大小
   * @param {string} time - 时间字符串（格式如 "05:30"）或文件大小（如 "4.49MB"）
   * @returns {string} - 格式化后的字符串
   */
  const formatTime = (time) => {
    if (!time) return '';
    
    // 如果是资源下载页面（first_title_id === 5），time 字段是文件大小，直接返回
    if (currentTitle.id === 5) {
      return `文件大小: ${time}`;
    }
    
    // 其他页面是视频时长，需要格式化
    const parts = time.split(':');
    if (parts.length !== 2) return time;
    
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    
    return `视频时长: ${minutes}m${seconds}s`;
  };

  useEffect(() => {
    if (currentTitle) {
      fetchVideoTitles(currentTitle.id);
    }
  }, [location.pathname]);

  // 当选中的二级或三级标题变化时，或者一级标题/二级标题数据变化时，重新获取视频内容
  useEffect(() => {
    // 🔥 关键：如果正在加载标题数据，跳过执行，避免使用旧数据
    if (isLoadingTitlesRef.current) {
      return;
    }
    
    // 必须等 videoData 有数据且有 second_titles 才执行
    if (currentTitle && selectedSecondId !== null && videoData && videoData.second_titles && videoData.second_titles.length > 0) {
      // 如果选中"全部"（selectedSecondId为'all'），则获取所有二级标题下的视频
      if (selectedSecondId === 'all') {
        fetchAllVideoContents(currentTitle.id);
      } else {
        fetchVideoContents(currentTitle.id, selectedSecondId, selectedThirdId);
      }
    }
  }, [selectedSecondId, selectedThirdId, currentTitle?.id, videoDataVersion]);

  /**
   * 获取视频标题数据
   */
  const fetchVideoTitles = async (firstTitleId) => {
    // 🔥 关键：设置加载标志，防止"视频内容 useEffect"使用旧数据
    isLoadingTitlesRef.current = true;
    setVideoData(null);
    setLoading(true);
    
    try {
      const response = await videoTitleAPI.getSecondTitles(firstTitleId);
      
      if (response.code === 200 && response.data) {
        // 一次性批量更新所有状态，避免中间状态触发 useEffect
        setVideoData(response.data);
        setSelectedSecondId('all');
        setVideoDataVersion(prev => prev + 1); // 最后增加版本号，确保数据已经准备好
      } else {
        message.error(response.message || '获取数据失败');
      }
    } catch (error) {
      message.error('获取数据失败，请稍后重试');
    } finally {
      // 🔥 关键：清除加载标志，允许"视频内容 useEffect"执行
      isLoadingTitlesRef.current = false;
      setLoading(false);
    }
  };

  /**
   * 获取所有二级标题下的视频内容（用于"全部"选项）
   */
  const fetchAllVideoContents = async (firstTitleId) => {
    setVideoLoading(true);
    try {
      if (!videoData || !videoData.second_titles || videoData.second_titles.length === 0) {
        setVideoContents([]);
        setVideoLoading(false);
        return;
      }

      // 遍历所有二级标题，获取每个二级标题下的视频内容
      const allVideoPromises = videoData.second_titles.map((secondTitle) => {
        return videoContentAPI.getContents(firstTitleId, secondTitle.id, null);
      });

      const responses = await Promise.all(allVideoPromises);
      
      // 合并所有视频内容
      const allVideos = [];
      responses.forEach(response => {
        if (response.code === 200 && response.data && response.data.video_contents) {
          allVideos.push(...response.data.video_contents);
        }
      });
      
      setVideoContents(allVideos);
    } catch (error) {
      message.error('获取视频内容失败，请稍后重试');
      setVideoContents([]);
    } finally {
      setVideoLoading(false);
    }
  };

  /**
   * 获取视频内容列表
   */
  const fetchVideoContents = async (firstTitleId, secondTitleId, thirdTitleId = null) => {
    setVideoLoading(true);
    try {
      const response = await videoContentAPI.getContents(firstTitleId, secondTitleId, thirdTitleId);
      
      if (response.code === 200 && response.data) {
        setVideoContents(response.data.video_contents || []);
      } else {
        message.error(response.message || '获取视频内容失败');
        setVideoContents([]);
      }
    } catch (error) {
      message.error('获取视频内容失败，请稍后重试');
      setVideoContents([]);
    } finally {
      setVideoLoading(false);
    }
  };

  /**
   * 处理二级标题点击
   */
  const handleSecondTitleClick = (secondTitleId) => {
    setSelectedSecondId(secondTitleId);
    setSelectedThirdId(null); // 切换二级标题时清空三级标题选择
  };

  /**
   * 处理三级标题点击
   */
  const handleThirdTitleClick = (thirdTitle) => {
    setSelectedThirdId(thirdTitle.id);
  };

  /**
   * 处理视频/资源点击
   */
  const handleVideoClick = (videoItem) => {
    // 如果是资源下载页面（first_title_id === 5），直接下载文件
    if (currentTitle.id === 5) {
      const fileUrl = `${VIDEO_BASE_URL}${videoItem.title}`;
      // 上报下载行为
      systemAPI.trackResourceAction('download', videoItem.title, videoItem.id);
      // 创建隐藏的 a 标签进行下载
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = videoItem.title;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('开始下载资源');
      return;
    }
    
    // 其他页面（1,2,3,4）播放视频
    // 上报观看视频行为
    systemAPI.trackResourceAction('watch', videoItem.title, videoItem.id);
    // 如果 title 不包含扩展名，自动添加 .mp4
    const fileName = videoItem.title.includes('.') ? videoItem.title : `${videoItem.title}.mp4`;
    const videoUrl = `${VIDEO_BASE_URL}${fileName}`;
    setPlayingVideo({
      ...videoItem,
      url: videoUrl
    });
    setIsPlayerVisible(true);
  };

  /**
   * 关闭视频播放器
   */
  const handleClosePlayer = () => {
    setIsPlayerVisible(false);
    // 延迟清空视频信息，等待动画结束
    setTimeout(() => {
      setPlayingVideo(null);
    }, 300);
  };

  /**
   * 处理资源预览
   */
  const handlePreview = (e, videoItem) => {
    e.stopPropagation(); // 阻止事件冒泡
    
    // 检查文件类型
    const extension = videoItem.title.split('.').pop().toLowerCase();
    const isPdf = extension === 'pdf';
    
    // 如果是 PDF 文件，检测浏览器是否支持在线阅读
    if (isPdf && !canReadPdfOnline()) {
      Modal.warning({
        title: '当前系统不支持在线阅读 PDF',
        content: (
          <div>
            <p>您的浏览器不支持在线预览 PDF 文件。</p>
            <p>这可能是因为：</p>
            <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
              <li>使用的是较旧版本的操作系统（如 Windows 7）</li>
              <li>浏览器版本过旧</li>
            </ul>
            <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
              请点击"下载"按钮下载文件后在本地查阅。
            </p>
          </div>
        ),
        okText: '我知道了',
        width: 480,
      });
      console.warn('[PDF预览] 当前浏览器不支持在线阅读 PDF');
      return;
    }
    
    // 查找当前文件在列表中的索引
    const index = videoContents.findIndex(v => v.id === videoItem.id);
    if (index !== -1) {
      setCurrentPreviewIndex(index);
      setPreviewVisible(true);
    }
  };

  /**
   * 关闭预览弹窗
   */
  const handleClosePreview = () => {
    setPreviewVisible(false);
    setCurrentPreviewIndex(0);
    setPageNumber(1); // 重置 PDF 页码
    setPdfScale(1.2); // 重置缩放比例
    setPdfError(null); // 重置错误状态
  };

  /**
   * 上一页（文档）
   */
  const handlePrevPage = () => {
    if (currentPreviewIndex > 0) {
      setCurrentPreviewIndex(currentPreviewIndex - 1);
      setPageNumber(1); // 切换文档时重置 PDF 页码
      setPdfScale(1.2); // 重置缩放比例
      setPdfError(null); // 重置错误状态
    }
  };

  /**
   * 下一页（文档）
   */
  const handleNextPage = () => {
    if (currentPreviewIndex < videoContents.length - 1) {
      setCurrentPreviewIndex(currentPreviewIndex + 1);
      setPageNumber(1); // 切换文档时重置 PDF 页码
      setPdfScale(1.2); // 重置缩放比例
      setPdfError(null); // 重置错误状态
    }
  };

  /**
   * PDF 文档加载成功回调
   */
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
  };

  /**
   * PDF 文档加载失败回调
   */
  const onDocumentLoadError = (error) => {
    let errorMessage = 'PDF 加载失败';
    if (error.message) {
      if (error.message.includes('CORS')) {
        errorMessage = '跨域访问被阻止，请检查服务器 CORS 配置';
      } else if (error.message.includes('404')) {
        errorMessage = 'PDF 文件不存在';
      } else if (error.message.includes('network')) {
        errorMessage = '网络连接失败';
      } else {
        errorMessage = error.message;
      }
    }
    
    setPdfError(errorMessage);
    message.error(`PDF 文件加载失败: ${errorMessage}`);
  };

  /**
   * PDF 页码切换
   */
  const handlePdfPageChange = (delta) => {
    setPageNumber(prev => {
      const newPage = prev + delta;
      if (newPage < 1) return 1;
      if (newPage > numPages) return numPages;
      return newPage;
    });
  };

  /**
   * PDF 缩放
   */
  const handlePdfZoom = (delta) => {
    setPdfScale(prev => {
      const newScale = prev + delta;
      if (newScale < 0.5) return 0.5;
      if (newScale > 3) return 3;
      return newScale;
    });
  };

  /**
   * 获取预览URL
   */
  const getPreviewUrl = (videoItem) => {
    const fileUrl = `${VIDEO_BASE_URL}${videoItem.title}`;
    const extension = videoItem.title.split('.').pop().toLowerCase();
    
    // 对于 Word 和 PPT 文件，使用 Microsoft Office Online Viewer
    if (['doc', 'docx', 'ppt', 'pptx'].includes(extension)) {
      return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
    } else if (extension === 'pdf') {
      // PDF 文件：隐藏左侧导航面板，但保留顶部工具栏（放大缩小等功能）
      return `${fileUrl}#navpanes=0`;
    } else {
      // 其他文件直接返回原始URL
      return fileUrl;
    }
  };

  /**
   * 处理资源下载
   */
  const handleDownload = async (e, videoItem) => {
    e.stopPropagation(); // 阻止事件冒泡
    const fileUrl = `${VIDEO_BASE_URL}${videoItem.title}`;
    
    // 上报下载行为
    systemAPI.trackResourceAction('download', videoItem.title, videoItem.id);
    
    try {
      message.loading({ content: '正在准备下载...', key: 'download', duration: 0 });
      
      // 使用 fetch 获取文件，启用 CORS
      const response = await fetch(fileUrl, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // 获取文件大小
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      // 读取响应体
      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        // 更新下载进度
        if (total > 0) {
          const percent = Math.round((receivedLength / total) * 100);
          message.loading({ 
            content: `正在下载 ${percent}%...`, 
            key: 'download', 
            duration: 0 
          });
        }
      }
      
      // 合并所有块
      const blob = new Blob(chunks);
      
      // 创建 Blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // 创建隐藏的 <a> 标签并触发下载
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = videoItem.title;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      message.success({ content: '下载成功！', key: 'download', duration: 2 });
    } catch (error) {
      message.error({ 
        content: `下载失败：${error.message}`, 
        key: 'download', 
        duration: 3 
      });
    }
  };

  /**
   * 获取视频缩略图URL或图标
   */
  const getVideoThumbnail = (videoItem) => {
    // 如果是资源下载页面（first_title_id === 5），根据文件后缀返回对应图标
    if (currentTitle.id === 5) {
      const extension = videoItem.title.split('.').pop().toLowerCase();
      if (extension === 'pdf') {
        return pdfIcon;
      } else if (extension === 'doc' || extension === 'docx') {
        return docxIcon;
      } else if (extension === 'ppt' || extension === 'pptx') {
        return pptIcon;
      }
      // 默认返回 pdf 图标
      return pdfIcon;
    }
    
    // 其他页面（1,2,3,4）使用视频缩略图
    // title 字段包含扩展名（如 "lesson01.mp4"），需要替换为 .png
    const titleWithoutExt = videoItem.title.replace(/\.[^.]+$/, '');
    return `${VIDEO_BASE_URL}${titleWithoutExt}.png`;
  };

  /**
   * 获取当前选中的二级标题
   */
  const getSelectedSecondTitle = () => {
    if (!videoData || !videoData.second_titles) return null;
    return videoData.second_titles.find(item => item.id === selectedSecondId);
  };

  /**
   * 检查是否有三级标题
   */
  const hasThirdTitles = () => {
    const selectedSecond = getSelectedSecondTitle();
    return selectedSecond && selectedSecond.third_titles && selectedSecond.third_titles.length > 0;
  };

  /**
   * 渲染内容区域
   */
  const renderContent = () => {
    if (!videoData || !videoData.second_titles || videoData.second_titles.length === 0) {
      return <Empty description="暂无内容" />;
    }

    const selectedSecond = getSelectedSecondTitle();

    return (
      <div className="content-box">
        {/* 第一行：二级标题 */}
        <div className="content-row">
          <span className="content-label">{currentTitle.name}：</span>
          <div className="content-items">
            {/* 全部选项 */}
            <span
              className={`content-item ${selectedSecondId === 'all' ? 'active' : ''}`}
              onClick={() => handleSecondTitleClick('all')}
            >
              全部
            </span>
            {videoData.second_titles.map((secondTitle) => (
              <span
                key={secondTitle.id}
                className={`content-item ${selectedSecondId === secondTitle.id ? 'active' : ''}`}
                onClick={() => handleSecondTitleClick(secondTitle.id)}
              >
                {secondTitle.title}
              </span>
            ))}
          </div>
        </div>

        {/* 第二行：三级标题（只在选中具体的二级标题且有三级标题时显示） */}
        {selectedSecondId !== 'all' && hasThirdTitles() && (
          <div className="content-row third-title-row">
            <span className="content-label">知识：</span>
            <div className="content-items">
              {/* 全部选项 */}
              <span
                className={`content-item ${selectedThirdId === null ? 'active' : ''}`}
                onClick={() => setSelectedThirdId(null)}
              >
                全部
              </span>
              {selectedSecond.third_titles.map((thirdTitle) => (
                <span
                  key={thirdTitle.id}
                  className={`content-item ${selectedThirdId === thirdTitle.id ? 'active' : ''}`}
                  onClick={() => handleThirdTitleClick(thirdTitle)}
                >
                  {thirdTitle.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * 根据 second_title_id 获取对应的标题名称
   */
  const getSecondTitleName = (secondTitleId) => {
    if (!videoData || !videoData.second_titles) {
      return '';
    }
    const titleObj = videoData.second_titles.find(item => item.id === secondTitleId);
    return titleObj ? titleObj.title : '';
  };

  /**
   * 渲染视频列表
   */
  const renderVideoList = () => {
    if (videoLoading) {
      return (
        <div className="video-list-empty">
          <Spin tip="加载视频中..." />
        </div>
      );
    }

    if (!videoContents || videoContents.length === 0) {
      return (
        <div className="video-list-empty">
          <Empty description="暂无视频内容" />
        </div>
      );
    }

    return (
      <div className="video-list-container">
        {videoContents.map((video) => (
          <div
            key={video.id}
            className="video-item"
            onClick={() => currentTitle.id !== 5 && handleVideoClick(video)}
          >
            {/* 视频缩略图 */}
            <div className="video-thumbnail">
              {/* 左上角红框标签 - 显示视频所属的二级标题 */}
              {(() => {
                const secondTitleName = getSecondTitleName(video.second_title_id);
                if (secondTitleName) {
                  return (
                    <div className="video-tag-container">
                      <img src={redFrameIcon} alt="tag" className="video-tag-icon" />
                      <span className="video-tag-text">{secondTitleName}</span>
                    </div>
                  );
                }
                return null;
              })()}
              <img
                src={getVideoThumbnail(video)}
                alt={video.small_name}
                className="video-thumbnail-image"
                onError={(e) => {
                  // 图片加载失败时显示默认占位图
                  e.target.style.display = 'none';
                }}
              />
              {/* 播放按钮 - 只在非资源下载页面显示 */}
              {currentTitle.id !== 5 && (
                <div className="video-play-icon">
                  <PlayCircleOutlined />
                </div>
              )}
            </div>
            
            {/* 视频信息 */}
            <div className="video-info">
              <div className="video-name" title={video.big_name}>
                {video.big_name}
              </div>
              <div className="video-small-name" title={video.small_name}>
                {video.small_name}
              </div>
              {/* 资源下载页面显示预览和下载按钮，其他页面显示时间 */}
              {currentTitle.id === 5 ? (
                <div className="video-actions">
                  <button 
                    className="action-btn preview-btn" 
                    onClick={(e) => handlePreview(e, video)}
                  >
                    <EyeOutlined /> 预览
                  </button>
                  <button 
                    className="action-btn download-btn" 
                    onClick={(e) => handleDownload(e, video)}
                  >
                    <DownloadOutlined /> 下载
                  </button>
                </div>
              ) : (
                <div className="video-time">{formatTime(video.time)}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 如果类型不存在，显示错误
  if (!currentTitle) {
    return (
      <div className="resource-extension-page">
        <Empty description="页面不存在" />
      </div>
    );
  }

  return (
    <div className="resource-extension-page">
      <Spin spinning={loading} tip="加载中...">
        {renderContent()}
        {renderVideoList()}
      </Spin>

      {/* 视频播放器弹窗 */}
      <Modal
        title={playingVideo?.small_name || '视频播放'}
        open={isPlayerVisible}
        onCancel={handleClosePlayer}
        footer={null}
        width="80%"
        centered
        destroyOnClose
        className="video-player-modal"
      >
        {playingVideo && (
          <div className="video-player-container">
            <video
              controls
              autoPlay
              controlsList="nodownload"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                backgroundColor: '#000'
              }}
            >
              <source src={playingVideo.url} type="video/mp4" />
              您的浏览器不支持视频播放。
            </video>
            {playingVideo.time && (
              <div className="video-player-info">
                <span>时长: {formatTime(playingVideo.time)}</span>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 文档预览弹窗 */}
      <Modal
        title={
          <div className="preview-modal-title">
            <div className="preview-title-left">
              <span className="preview-doc-name">{videoContents[currentPreviewIndex]?.big_name || '文档预览'}</span>
              <span className="preview-page-info">
                {currentPreviewIndex + 1} / {videoContents.length}
              </span>
            </div>
            {/* 如果是PDF，显示缩放控制 */}
            {videoContents[currentPreviewIndex] && 
             videoContents[currentPreviewIndex].title.split('.').pop().toLowerCase() === 'pdf' && (
              <div className="preview-title-controls">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePdfZoom(-0.2);
                  }}
                  disabled={pdfScale <= 0.5}
                  className="title-control-btn"
                >
                  <ZoomOutOutlined /> 缩小
                </button>
                <span className="title-scale-text">{Math.round(pdfScale * 100)}%</span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePdfZoom(0.2);
                  }}
                  disabled={pdfScale >= 3}
                  className="title-control-btn"
                >
                  <ZoomInOutlined /> 放大
                </button>
                <span className="title-divider"></span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDoublePage(!isDoublePage);
                  }}
                  className={`title-control-btn ${isDoublePage ? 'active' : ''}`}
                >
                  {isDoublePage ? '单页' : '双页'}
                </button>
                <span className="title-page-text">
                  共 {numPages || '...'} 页
                </span>
              </div>
            )}
          </div>
        }
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={null}
        width="90%"
        centered
        destroyOnClose
        className="preview-modal"
      >
        {videoContents[currentPreviewIndex] && (() => {
          const currentFile = videoContents[currentPreviewIndex];
          const extension = currentFile.title.split('.').pop().toLowerCase();
          const isPdf = extension === 'pdf';
          const fileUrl = `${VIDEO_BASE_URL}${currentFile.title}`;

          return (
            <div className="preview-container">
              {/* 上一篇按钮 */}
              <button
                className={`preview-nav-btn prev-btn ${currentPreviewIndex === 0 ? 'disabled' : ''}`}
                onClick={handlePrevPage}
                disabled={currentPreviewIndex === 0}
              >
                <LeftOutlined />
                <span className="nav-btn-text">上一篇</span>
              </button>

              {/* 文档预览区域 */}
              <div className="preview-content">
                {isPdf ? (
                  <div className={`pdf-document-wrapper pdf-scroll-container ${isDoublePage ? 'double-page-mode' : ''}`}>
                      <Document
                        file={pdfFile}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={
                          <div className="pdf-loading">
                            <Spin tip="加载 PDF 中..." />
                          </div>
                        }
                        error={
                          <div className="pdf-error">
                            <p>PDF 加载失败</p>
                            {pdfError && <p className="error-details">{pdfError}</p>}
                            <p className="error-hint">请检查文件是否存在或稍后重试</p>
                          </div>
                        }
                        options={pdfOptions}
                      >
                        {/* 渲染所有页面，支持连续滚动和双页显示 */}
                        {numPages && (() => {
                          if (isDoublePage) {
                            // 双页模式：每行显示两页
                            const pageGroups = [];
                            for (let i = 0; i < numPages; i += 2) {
                              pageGroups.push(
                                <div key={`page_group_${i}`} className="pdf-page-row">
                                  <div className="pdf-page-container">
                                    <Page 
                                      pageNumber={i + 1} 
                                      scale={pdfScale}
                                      renderTextLayer={true}
                                      renderAnnotationLayer={false}
                                      loading={
                                        <div className="pdf-page-loading">
                                          <Spin size="small" />
                                          <p>加载第 {i + 1} 页...</p>
                                        </div>
                                      }
                                    />
                                    <div className="pdf-page-number">第 {i + 1} 页</div>
                                  </div>
                                  {i + 1 < numPages && (
                                    <div className="pdf-page-container">
                                      <Page 
                                        pageNumber={i + 2} 
                                        scale={pdfScale}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={false}
                                        loading={
                                          <div className="pdf-page-loading">
                                            <Spin size="small" />
                                            <p>加载第 {i + 2} 页...</p>
                                          </div>
                                        }
                                      />
                                      <div className="pdf-page-number">第 {i + 2} 页</div>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return pageGroups;
                          } else {
                            // 单页模式：每页独立显示
                            return Array.from(new Array(numPages), (el, index) => (
                              <div key={`page_${index + 1}`} className="pdf-page-container">
                                <Page 
                                  pageNumber={index + 1} 
                                  scale={pdfScale}
                                  renderTextLayer={true}
                                  renderAnnotationLayer={false}
                                  loading={
                                    <div className="pdf-page-loading">
                                      <Spin size="small" />
                                      <p>加载第 {index + 1} 页...</p>
                                    </div>
                                  }
                                />
                                <div className="pdf-page-number">第 {index + 1} 页</div>
                              </div>
                            ));
                          }
                        })()}
                      </Document>
                  </div>
                ) : (
                  <iframe
                    src={getPreviewUrl(currentFile)}
                    title="文档预览"
                    className="preview-iframe"
                  />
                )}
              </div>

              {/* 下一篇按钮 */}
              <button
                className={`preview-nav-btn next-btn ${currentPreviewIndex === videoContents.length - 1 ? 'disabled' : ''}`}
                onClick={handleNextPage}
                disabled={currentPreviewIndex === videoContents.length - 1}
              >
                <span className="nav-btn-text">下一篇</span>
                <RightOutlined />
              </button>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default ResourceExtension;

